import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Row, Col, Button } from "react-bootstrap";
import Loader from "../../../components/loader/index.js";
import axiosClient from "../../../config/axios.js";
import axios from "axios";
import qs from "qs";
import {
  GoogleMap,
  LoadScript,
  Marker,
  Circle,
  Polyline,
} from "@react-google-maps/api";
import BasePointSearch from "./BasePointSearch";
import NearbyOrdersList from "../orders/orderMapModal/NearbyOrdersList";
import MapFilters from "../orders/orderMapModal/MapFilters";
import NearbyOrdersMapsSkeleton from "./NearbyOrdersMapsSkeleton";

const NearbyOrdersMaps = () => {
  const [loading, setLoading] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [nearbyPoints, setNearbyPoints] = useState([]);
  const [radius, setRadius] = useState(5000); // 5km por defecto
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showAllRadii, setShowAllRadii] = useState(false);
  const [extendedSearch, setExtendedSearch] = useState(null); // Estado para búsqueda extendida de 100 km
  const [basePointSearchValue, setBasePointSearchValue] = useState("");
  const [rightClickCoordinates, setRightClickCoordinates] = useState(null); // Coordenadas del clic derecho con su dirección
  const [searchResults, setSearchResults] = useState([]); // Resultados de búsqueda del BasePointSearch
  const [filters, setFilters] = useState({
    craneTypes: [],
    vehicleTypeId: null,
    clientTypeId: null,
    insuranceId: null,
  });
  const excludedOrderIds = useRef([]);
  const handleFetchNearbyPointsRef = useRef(null);
  const directionsCache = useRef({});
  const basePointSearchRef = useRef(null); // Referencia para llamar a handleSearch del BasePointSearch
  // Cache para guardar los datos de todos los radios por punto base
  // Estructura: { "lat-lng": { allRadii: [...], radius5000: [...], radius10000: [...], radius15000: [...] } }
  const cachedPointData = useRef({});
  const mapRef = useRef(null); // Referencia al mapa de Google Maps
  const mapContainerRef = useRef(null); // Referencia al contenedor del mapa
  const isManualSearchRef = useRef(false); // Bandera para indicar que la búsqueda es manual (desde el botón)
  const isFetchingRef = useRef(false); // Bandera para evitar múltiples llamadas simultáneas a fetchAllRadii

  // Verificar cuando Google Maps está cargado
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setIsGoogleMapsLoaded(true);
      }
    };

    // Verificar inmediatamente
    checkGoogleMaps();

    // Verificar periódicamente hasta que esté cargado
    const interval = setInterval(() => {
      if (window.google && window.google.maps) {
        setIsGoogleMapsLoaded(true);
        clearInterval(interval);
      }
    }, 100);

    // Limpiar intervalo después de 10 segundos
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Función helper para obtener el min_radius basado en el radius
  const getMinRadius = useCallback((radiusValue) => {
    if (radiusValue <= 5000) return 1; // 1 a 5000
    if (radiusValue <= 10000) return 5001; // 5001 a 10000
    if (radiusValue <= 15000) return 10001; // 10001 a 15000
    return 0; // Por defecto
  }, []);

  // Función helper para obtener la clave del caché de un punto
  const getPointCacheKey = useCallback((location) => {
    if (!location || !location.latitude || !location.longitude) return null;
    return `${Number(location.latitude).toFixed(6)}-${Number(
      location.longitude
    ).toFixed(6)}`;
  }, []);

  const fetchNearbyPoints = useCallback(
    async (
      location,
      customRadius = null,
      customMinRadius = null,
      excludedIds = null,
      customFilters = null
    ) => {
      if (!location) return [];
      try {
        // Usar filtros personalizados o los del estado
        const filtersToUse = customFilters || filters;

        // Extraer tipos desde los filtros
        const tipos = filtersToUse.craneTypes
          ? filtersToUse.craneTypes.map((ct) => ct.value)
          : [];

        const radiusToUse = customRadius || radius;
        const minRadiusToUse =
          customMinRadius !== null
            ? customMinRadius
            : getMinRadius(radiusToUse);
        const excludedToUse =
          excludedIds !== null ? excludedIds : excludedOrderIds.current;

        const response = await axiosClient.get(`/locations/nearbyMap`, {
          params: {
            longitude: Number(location.longitude),
            latitude: Number(location.latitude),
            radius: radiusToUse,
            min_radius: minRadiusToUse,
            tipos, // array limpio: ["TIPO A", "TIPO B"]
            order_id: null, // No hay orden específica en esta página
            excluded_ids: excludedToUse,
            insurance_id: filtersToUse.insuranceId?.value || null,
            client_type_id: filtersToUse.clientTypeId?.value || null,
            vehicle_type_id: filtersToUse.vehicleTypeId?.value || null,
          },
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "repeat" }),
        });

        const data = response.data?.data || [];

        // Actualizar excluded_ids con los nuevos order_ids encontrados
        data.forEach((item) => {
          if (!excludedOrderIds.current.includes(item.order_id)) {
            excludedOrderIds.current.push(item.order_id);
          }
        });

        return data;
      } catch (err) {
        console.error("Error al obtener puntos cercanos:", err);
        return [];
      }
    },
    [radius, getMinRadius, filters]
  );

  const fetchAllRadii = useCallback(
    async (location) => {
      if (!location) return;

      const cacheKey = getPointCacheKey(location);
      if (!cacheKey) return;

      // Verificar si ya tenemos datos en caché para este punto
      if (cachedPointData.current[cacheKey]?.allRadii) {
        // Si hay caché y se está haciendo una búsqueda manual, resetear la bandera
        if (isManualSearchRef.current) {
          isFetchingRef.current = false;
        }
        const cachedData = cachedPointData.current[cacheKey];
        const allResults = [];
        cachedData.allRadii.forEach((batch) => {
          allResults.push(...batch.data);
        });

        // Restaurar búsqueda extendida del caché si existe
        if (cachedData.extendedSearch) {
          setExtendedSearch(cachedData.extendedSearch);
          // Agregar resultados de búsqueda extendida si existen
          if (
            cachedData.extendedSearch.data &&
            cachedData.extendedSearch.data.length > 0
          ) {
            allResults.push(...cachedData.extendedSearch.data);
          }
        } else {
          setExtendedSearch(null);
        }

        // Eliminar duplicados por order_id
        const uniqueResults = [];
        const seenOrderIds = new Set();
        allResults.forEach((item) => {
          if (!seenOrderIds.has(item.order_id)) {
            seenOrderIds.add(item.order_id);
            uniqueResults.push(item);
          }
        });

        // Actualizar excluded_ids
        uniqueResults.forEach((item) => {
          if (!excludedOrderIds.current.includes(item.order_id)) {
            excludedOrderIds.current.push(item.order_id);
          }
        });

        setNearbyPoints(uniqueResults);
        setSelectedOrderId(null);
        setShowAllRadii(true);
        return;
      }

      // Evitar ejecuciones duplicadas - verificar justo antes de hacer la petición HTTP
      if (isFetchingRef.current && !isManualSearchRef.current) {
        return;
      }

      // Marcar que se está ejecutando una búsqueda
      isFetchingRef.current = true;
      setLoading(true);
      setShowAllRadii(true);
      excludedOrderIds.current = []; // Resetear excluded_ids

      try {
        // Extraer tipos desde los filtros
        const tipos = filters.craneTypes
          ? filters.craneTypes.map((ct) => ct.value)
          : [];

        // Llamar al nuevo endpoint batch
        const response = await axiosClient.post(`/locations/nearbyMapBatch`, {
          longitude: Number(location.longitude),
          latitude: Number(location.latitude),
          radius_configs: [
            { radius: 5000, min_radius: 1 },
            { radius: 10000, min_radius: 5001 },
            { radius: 15000, min_radius: 10001 },
          ],
          tipos,
          order_id: null, // No hay orden específica en esta página
          excluded_ids: [],
          insurance_id: filters.insuranceId?.value || null,
          client_type_id: filters.clientTypeId?.value || null,
          vehicle_type_id: filters.vehicleTypeId?.value || null,
        });

        const batchResults = response.data?.data || [];
        const extendedSearchData = response.data?.extendedSearch || null;

        // Guardar la información de búsqueda extendida
        setExtendedSearch(extendedSearchData);

        // Guardar los datos en caché por punto base
        if (!cachedPointData.current[cacheKey]) {
          cachedPointData.current[cacheKey] = {};
        }
        cachedPointData.current[cacheKey].allRadii = batchResults;
        // Guardar también la búsqueda extendida en el caché
        if (extendedSearchData) {
          cachedPointData.current[cacheKey].extendedSearch = extendedSearchData;
        }

        // También guardar datos individuales por radio para acceso rápido
        batchResults.forEach((batch) => {
          if (batch.radius === 5000 && batch.min_radius === 1) {
            cachedPointData.current[cacheKey].radius5000 = batch.data;
          } else if (batch.radius === 10000 && batch.min_radius === 5001) {
            cachedPointData.current[cacheKey].radius10000 = batch.data;
          } else if (batch.radius === 15000 && batch.min_radius === 10001) {
            cachedPointData.current[cacheKey].radius15000 = batch.data;
          }
        });

        // Combinar todos los resultados (incluyendo búsqueda extendida si existe)
        const allResults = [];
        batchResults.forEach((batch) => {
          allResults.push(...batch.data);
        });

        // Agregar resultados de búsqueda extendida si existe
        if (
          extendedSearchData &&
          extendedSearchData.data &&
          extendedSearchData.data.length > 0
        ) {
          allResults.push(...extendedSearchData.data);
        }

        // Eliminar duplicados por order_id
        const uniqueResults = [];
        const seenOrderIds = new Set();
        allResults.forEach((item) => {
          if (!seenOrderIds.has(item.order_id)) {
            seenOrderIds.add(item.order_id);
            uniqueResults.push(item);
          }
        });

        // Actualizar excluded_ids con los nuevos order_ids encontrados
        uniqueResults.forEach((item) => {
          if (!excludedOrderIds.current.includes(item.order_id)) {
            excludedOrderIds.current.push(item.order_id);
          }
        });

        setNearbyPoints(uniqueResults);
        setSelectedOrderId(null);
      } catch (err) {
        console.error("Error al obtener puntos de todos los radios:", err);
      } finally {
        setLoading(false);
        isFetchingRef.current = false; // Resetear la bandera al finalizar
      }
    },
    [getPointCacheKey, filters]
  );

  const handleFetchNearbyPoints = useCallback(
    async (location) => {
      if (!location) return;

      const cacheKey = getPointCacheKey(location);
      if (!cacheKey) return;

      // Verificar si ya tenemos datos en caché para este punto y radio
      const cacheKeyForRadius = `radius${radius}`;
      if (cachedPointData.current[cacheKey]?.[cacheKeyForRadius]) {
        const cachedData = cachedPointData.current[cacheKey][cacheKeyForRadius];

        // Actualizar excluded_ids
        cachedData.forEach((item) => {
          if (!excludedOrderIds.current.includes(item.order_id)) {
            excludedOrderIds.current.push(item.order_id);
          }
        });

        setNearbyPoints(cachedData);
        setSelectedOrderId(null);
        setShowAllRadii(false);
        return;
      }

      setLoading(true);
      setShowAllRadii(false);
      excludedOrderIds.current = []; // Resetear excluded_ids

      try {
        const data = await fetchNearbyPoints(
          location,
          null,
          null,
          null,
          filters
        );

        // Guardar en caché
        if (!cachedPointData.current[cacheKey]) {
          cachedPointData.current[cacheKey] = {};
        }
        cachedPointData.current[cacheKey][cacheKeyForRadius] = data;

        setNearbyPoints(data);
        setSelectedOrderId(null);
      } finally {
        setLoading(false);
      }
    },
    [fetchNearbyPoints, radius, getPointCacheKey, filters]
  );

  // Actualizar la referencia de handleFetchNearbyPoints
  useEffect(() => {
    handleFetchNearbyPointsRef.current = handleFetchNearbyPoints;
  }, [handleFetchNearbyPoints]);

  // Limpiar puntos cuando no hay tipos de grúa seleccionados
  useEffect(() => {
    if (!filters.craneTypes || filters.craneTypes.length === 0) {
      setNearbyPoints([]);
      setSelectedOrderId(null);
      setExtendedSearch(null);
    }
  }, [filters.craneTypes]);

  const handleSelectPoint = useCallback((location) => {
    if (location === null) {
      // Si se limpia la selección, limpiar todos los caches y datos
      setSelectedPoint(null);
      setNearbyPoints([]);
      setSelectedOrderId(null);
      setExtendedSearch(null);
      setShowAllRadii(false);
      setBasePointSearchValue(""); // Limpiar también el input de búsqueda
      setSearchResults([]); // Limpiar resultados de búsqueda
      cachedPointData.current = {};
      excludedOrderIds.current = [];
      directionsCache.current = {};
    } else {
      setSelectedPoint(location);
      // Limpiar resultados de búsqueda solo cuando se selecciona un punto base definitivo
      setSearchResults([]);
      // No hacer fetch aquí, el useEffect se encargará de cargar los datos
      // Esto permite que use el caché si está disponible
    }
  }, []);

  // Función para manejar clic derecho en el mapa
  const handleMapRightClick = useCallback((e) => {
    if (!e || !e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    // Formatear coordenadas para el input
    const coordinatesString = `${lat}, ${lng}`;
    setBasePointSearchValue(coordinatesString);

    // Limpiar resultados de búsqueda cuando se hace clic derecho
    setSearchResults([]);

    // Actualizar las coordenadas INMEDIATAMENTE para mostrar el punto sin demora
    setRightClickCoordinates({
      latitude: lat,
      longitude: lng,
      direccion: "", // Se actualizará cuando llegue la respuesta
      coordinatesString: coordinatesString,
    });

    // Obtener la dirección formateada de forma asíncrona en segundo plano
    // (sin bloquear la actualización visual)
    axios
      .get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      )
      .then((response) => {
        const formattedAddress = response.data.display_name || "";

        // Actualizar solo la dirección cuando esté lista
        setRightClickCoordinates((prev) => ({
          ...prev,
          direccion: formattedAddress,
        }));
      })
      .catch((error) => {
        console.error("Error al obtener dirección:", error);
        // Si hay error, mantener las coordenadas pero limpiar la dirección
        setRightClickCoordinates((prev) => ({
          ...prev,
          direccion: "",
        }));
      });
  }, []);

  // Prevenir el menú contextual del navegador en el mapa - DESDE EL INICIO
  useEffect(() => {
    // Prevenir el menú contextual inmediatamente cuando el componente se monta
    // Esto asegura que no aparezca el diálogo del navegador ni siquiera la primera vez
    const handleContextMenu = (e) => {
      // Solo prevenir si el clic es dentro del contenedor del mapa
      if (mapContainerRef.current && mapContainerRef.current.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Agregar listener al documento desde el inicio (capture phase para capturarlo temprano)
    document.addEventListener("contextmenu", handleContextMenu, true);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu, true);
    };
  }, []); // Sin dependencias para que se ejecute solo una vez al montar

  // Función para manejar la selección de un resultado de búsqueda desde el mapa
  const handleSelectSearchResult = useCallback((result) => {
    const location = {
      latitude: result.latitude,
      longitude: result.longitude,
      direccion: result.direccion || "",
      referencia: "",
    };

    // Formatear coordenadas para el input
    const coordinatesString = `${result.latitude}, ${result.longitude}`;
    setBasePointSearchValue(coordinatesString);

    // Establecer como coordenadas del clic derecho (que se mostrará en "Coordenadas seleccionadas")
    setRightClickCoordinates({
      latitude: result.latitude,
      longitude: result.longitude,
      direccion: result.direccion || "",
      coordinatesString: coordinatesString,
    });

    // Limpiar resultados de búsqueda cuando se selecciona una sugerencia del mapa
    setSearchResults([]);
  }, []);

  // Función para manejar la búsqueda desde los filtros
  const handleSearch = useCallback(
    (searchFilters) => {
      // Validar que se haya seleccionado al menos un tipo de grúa
      if (!searchFilters?.craneTypes || searchFilters.craneTypes.length === 0) {
        return; // No ejecutar búsqueda si no hay tipos de grúa seleccionados
      }

      // Limpiar caché cuando cambian los filtros
      cachedPointData.current = {};
      excludedOrderIds.current = [];
      setNearbyPoints([]);
      setSelectedOrderId(null);
      setExtendedSearch(null); // Limpiar búsqueda extendida

      // Si hay un punto seleccionado, recargar los datos con los nuevos filtros
      if (selectedPoint) {
        setShowAllRadii(true);
        fetchAllRadii(selectedPoint);
      }
    },
    [selectedPoint, fetchAllRadii]
  );

  // Función para verificar si hay filtros seleccionados
  const hasFilters = useCallback(() => {
    return (
      (filters.craneTypes && filters.craneTypes.length > 0) ||
      filters.vehicleTypeId !== null ||
      filters.clientTypeId !== null ||
      filters.insuranceId !== null
    );
  }, [filters]);

  // Función para ejecutar la búsqueda de órdenes cercanas
  const executeNearbyOrdersSearch = useCallback(() => {
    if (!selectedPoint) return;

    // Marcar que la búsqueda es manual y que está en curso ANTES de cualquier cambio de estado
    // para evitar que los useEffects se ejecuten
    isManualSearchRef.current = true;
    isFetchingRef.current = true;

    // Limpiar caché cuando se hace búsqueda
    cachedPointData.current = {};
    excludedOrderIds.current = [];
    setNearbyPoints([]);
    setSelectedOrderId(null);
    setExtendedSearch(null);

    // Ejecutar búsqueda con los filtros actuales (pueden estar vacíos) y el punto base seleccionado
    // Si no hay filtros, solo buscará por ubicación
    // setShowAllRadii se llamará dentro de fetchAllRadii, así que no lo llamamos aquí
    fetchAllRadii(selectedPoint).finally(() => {
      // Resetear las banderas después de que termine la búsqueda
      // Usar setTimeout para asegurar que los useEffects no se ejecuten inmediatamente
      setTimeout(() => {
        isManualSearchRef.current = false;
        isFetchingRef.current = false;
      }, 100);
    });
  }, [selectedPoint, fetchAllRadii]);

  // Función para manejar la búsqueda combinada (filtros + punto base)
  const handleCombinedSearch = useCallback(async () => {
    // Validar que se haya seleccionado al menos un tipo de grúa
    if (!filters.craneTypes || filters.craneTypes.length === 0) {
      alert("Debe seleccionar al menos un tipo de grúa para realizar la búsqueda");
      return;
    }

    // Si hay coordenadas del clic derecho pero no hay punto seleccionado, primero seleccionar ese punto
    if (rightClickCoordinates && !selectedPoint) {
      const location = {
        latitude: rightClickCoordinates.latitude,
        longitude: rightClickCoordinates.longitude,
        direccion: rightClickCoordinates.direccion,
        referencia: "",
      };
      handleSelectPoint(location);
      setRightClickCoordinates(null); // Limpiar las coordenadas del clic derecho
      // Esperar un momento para que se procese la selección
      setTimeout(() => {
        // El useEffect que escucha selectedPoint se encargará de buscar las órdenes cercanas
      }, 300);
      return;
    }

    // Si hay texto en el input pero no hay punto seleccionado, primero buscar el punto base
    if (basePointSearchValue.trim() && !selectedPoint) {
      if (basePointSearchRef.current) {
        // Llamar a la función de búsqueda del BasePointSearch
        await basePointSearchRef.current();
        // Esperar un momento para que se procese la búsqueda y se seleccione el punto automáticamente
        // (si hay un solo resultado). Luego el useEffect que escucha selectedPoint se encargará de buscar las órdenes cercanas
        setTimeout(() => {
          // Si después de la búsqueda hay un punto seleccionado, buscar órdenes cercanas
          // Esto se maneja automáticamente por el useEffect de selectedPoint
        }, 300);
        return;
      }
    }

    // Si no hay punto seleccionado y no hay filtros, no hacer nada
    if (!selectedPoint && !hasFilters()) {
      return;
    }

    // Si no hay punto seleccionado pero hay filtros, no se puede buscar sin punto base
    if (!selectedPoint) {
      return;
    }

    // Si hay punto seleccionado, ejecutar búsqueda de órdenes cercanas
    executeNearbyOrdersSearch();
  }, [
    selectedPoint,
    basePointSearchValue,
    rightClickCoordinates,
    hasFilters,
    executeNearbyOrdersSearch,
    handleSelectPoint,
    filters,
  ]);

  // No limpiar automáticamente el caché cuando cambian los filtros
  // Solo se limpiará cuando el usuario haga clic en "Buscar"

  // Cargar datos cuando cambia el punto base seleccionado
  useEffect(() => {
    // Si la búsqueda es manual (desde el botón) o ya hay una búsqueda en curso, no ejecutar este useEffect
    if (isManualSearchRef.current || isFetchingRef.current) {
      return;
    }

    // Validar que se haya seleccionado al menos un tipo de grúa
    if (!filters.craneTypes || filters.craneTypes.length === 0) {
      return; // No cargar datos si no hay tipos de grúa seleccionados
    }

    if (selectedPoint) {
      const cacheKey = getPointCacheKey(selectedPoint);

      // Siempre intentar cargar todos los radios primero (como con el primer punto)
      // Si ya hay datos en caché, usarlos; si no, hacer fetchAllRadii
      if (cacheKey && cachedPointData.current[cacheKey]?.allRadii) {
        // Ya hay datos en caché, usar los datos según el modo actual
        const cachedData = cachedPointData.current[cacheKey];

        if (!showAllRadii) {
          // Modo radio específico - extraer datos del radio específico desde allRadii
          const cacheKeyForRadius = `radius${radius}`;
          let cachedDataForRadius = cachedData[cacheKeyForRadius];

          // Si no está guardado individualmente, extraerlo de allRadii
          if (!cachedDataForRadius) {
            const batch = cachedData.allRadii.find(
              (b) =>
                b.radius === radius && b.min_radius === getMinRadius(radius)
            );
            if (batch) {
              cachedDataForRadius = batch.data;
              // Guardar para acceso rápido futuro
              cachedData[cacheKeyForRadius] = cachedDataForRadius;
            }
          }

          if (cachedDataForRadius) {
            // Actualizar excluded_ids
            cachedDataForRadius.forEach((item) => {
              if (!excludedOrderIds.current.includes(item.order_id)) {
                excludedOrderIds.current.push(item.order_id);
              }
            });

            setNearbyPoints(cachedDataForRadius);
            setSelectedOrderId(null);
            setShowAllRadii(false);
            return;
          }
        } else {
          // Modo "Todos"
          const allResults = [];
          cachedData.allRadii.forEach((batch) => {
            allResults.push(...batch.data);
          });

          // Restaurar y agregar datos de búsqueda extendida si existen
          if (cachedData.extendedSearch) {
            setExtendedSearch(cachedData.extendedSearch);
            if (
              cachedData.extendedSearch.data &&
              cachedData.extendedSearch.data.length > 0
            ) {
              allResults.push(...cachedData.extendedSearch.data);
            }
          } else {
            setExtendedSearch(null);
          }

          // Eliminar duplicados por order_id
          const uniqueResults = [];
          const seenOrderIds = new Set();
          allResults.forEach((item) => {
            if (!seenOrderIds.has(item.order_id)) {
              seenOrderIds.add(item.order_id);
              uniqueResults.push(item);
            }
          });

          // Actualizar excluded_ids
          uniqueResults.forEach((item) => {
            if (!excludedOrderIds.current.includes(item.order_id)) {
              excludedOrderIds.current.push(item.order_id);
            }
          });

          setNearbyPoints(uniqueResults);
          setSelectedOrderId(null);
          setShowAllRadii(true);
          return;
        }
      } else {
        // No hay datos en caché, hacer fetchAllRadii para cargar todos los datos de una vez
        // Esto asegura que todos los radios estén disponibles en caché
        setShowAllRadii(true);
        fetchAllRadii(selectedPoint);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoint, filters.craneTypes]);

  // Actualizar puntos cuando cambia el radio o el modo (Todos/radio específico)
  useEffect(() => {
    // Si la búsqueda es manual (desde el botón) o ya hay una búsqueda en curso, no ejecutar este useEffect
    if (isManualSearchRef.current || isFetchingRef.current) {
      return;
    }

    // Validar que se haya seleccionado al menos un tipo de grúa
    if (!filters.craneTypes || filters.craneTypes.length === 0) {
      return; // No actualizar puntos si no hay tipos de grúa seleccionados
    }

    if (selectedPoint) {
      const cacheKey = getPointCacheKey(selectedPoint);

      // Siempre usar datos del caché si están disponibles
      if (cacheKey && cachedPointData.current[cacheKey]?.allRadii) {
        const cachedData = cachedPointData.current[cacheKey];

        if (!showAllRadii) {
          // Modo radio específico - extraer datos del radio específico desde allRadii
          const cacheKeyForRadius = `radius${radius}`;
          let cachedDataForRadius = cachedData[cacheKeyForRadius];

          // Si no está guardado individualmente, extraerlo de allRadii
          if (!cachedDataForRadius) {
            const batch = cachedData.allRadii.find(
              (b) =>
                b.radius === radius && b.min_radius === getMinRadius(radius)
            );
            if (batch) {
              cachedDataForRadius = batch.data;
              // Guardar para acceso rápido futuro
              cachedData[cacheKeyForRadius] = cachedDataForRadius;
            }
          }

          if (cachedDataForRadius) {
            // Actualizar excluded_ids
            cachedDataForRadius.forEach((item) => {
              if (!excludedOrderIds.current.includes(item.order_id)) {
                excludedOrderIds.current.push(item.order_id);
              }
            });

            setNearbyPoints(cachedDataForRadius);
            setSelectedOrderId(null);
            return;
          }
        } else {
          // Modo "Todos"
          const allResults = [];
          cachedData.allRadii.forEach((batch) => {
            allResults.push(...batch.data);
          });

          // Restaurar y agregar datos de búsqueda extendida si existen
          if (cachedData.extendedSearch) {
            setExtendedSearch(cachedData.extendedSearch);
            if (
              cachedData.extendedSearch.data &&
              cachedData.extendedSearch.data.length > 0
            ) {
              allResults.push(...cachedData.extendedSearch.data);
            }
          } else {
            setExtendedSearch(null);
          }

          // Eliminar duplicados por order_id
          const uniqueResults = [];
          const seenOrderIds = new Set();
          allResults.forEach((item) => {
            if (!seenOrderIds.has(item.order_id)) {
              seenOrderIds.add(item.order_id);
              uniqueResults.push(item);
            }
          });

          // Actualizar excluded_ids
          uniqueResults.forEach((item) => {
            if (!excludedOrderIds.current.includes(item.order_id)) {
              excludedOrderIds.current.push(item.order_id);
            }
          });

          setNearbyPoints(uniqueResults);
          setSelectedOrderId(null);
          return;
        }
      }

      // Si no hay datos en caché, esto no debería pasar si fetchAllRadii se ejecutó correctamente
      // Pero por seguridad, si no hay datos, hacer fetchAllRadii
      if (!cacheKey || !cachedPointData.current[cacheKey]?.allRadii) {
        setShowAllRadii(true);
        fetchAllRadii(selectedPoint);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius, showAllRadii, filters.craneTypes]);

  // Calcular el centro del mapa considerando resultados de búsqueda
  const center = useMemo(() => {
    if (selectedPoint) {
      return {
        lat: Number(selectedPoint.latitude),
        lng: Number(selectedPoint.longitude),
      };
    }
    if (rightClickCoordinates) {
      return {
        lat: Number(rightClickCoordinates.latitude),
        lng: Number(rightClickCoordinates.longitude),
      };
    }
    if (searchResults && searchResults.length > 0) {
      // Si hay resultados de búsqueda, centrar en el primer resultado
      return {
        lat: Number(searchResults[0].latitude),
        lng: Number(searchResults[0].longitude),
      };
    }
    return {
      lat: 17.05,
      lng: -96.72,
    };
  }, [selectedPoint, rightClickCoordinates, searchResults]);

  // Ajustar la vista del mapa cuando hay resultados de búsqueda
  useEffect(() => {
    if (
      mapRef.current &&
      searchResults &&
      searchResults.length > 0 &&
      !selectedPoint &&
      isGoogleMapsLoaded &&
      window.google &&
      window.google.maps
    ) {
      const timeoutId = setTimeout(() => {
        if (mapRef.current) {
          const bounds = new window.google.maps.LatLngBounds();
          searchResults.forEach((result) => {
            bounds.extend({
              lat: Number(result.latitude),
              lng: Number(result.longitude),
            });
          });
          mapRef.current.fitBounds(bounds, { padding: 50 });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [searchResults, selectedPoint, isGoogleMapsLoaded]);

  // Ajustar la vista del mapa cuando hay un círculo extendido
  useEffect(() => {
    if (
      mapRef.current &&
      selectedPoint &&
      extendedSearch &&
      extendedSearch.radius &&
      isGoogleMapsLoaded &&
      window.google &&
      window.google.maps
    ) {
      // Usar setTimeout para asegurar que el círculo se haya renderizado
      const timeoutId = setTimeout(() => {
        if (mapRef.current) {
          // Crear bounds que incluyan el círculo extendido
          const bounds = new window.google.maps.LatLngBounds();
          const centerLat = Number(selectedPoint.latitude);
          const centerLng = Number(selectedPoint.longitude);

          // Calcular los puntos del círculo extendido para incluirlos en los bounds
          // Usar un círculo aproximado con 8 puntos para crear los bounds
          const radiusInMeters = extendedSearch.radius;
          const radiusInDegrees = radiusInMeters / 111320; // Aproximación: 1 grado ≈ 111320 metros

          // Agregar el centro
          bounds.extend({ lat: centerLat, lng: centerLng });

          // Agregar puntos alrededor del círculo para asegurar que esté visible
          for (let i = 0; i < 8; i++) {
            const angle = (i * Math.PI * 2) / 8;
            const lat = centerLat + radiusInDegrees * Math.cos(angle);
            const lng =
              centerLng +
              (radiusInDegrees * Math.sin(angle)) /
                Math.cos((centerLat * Math.PI) / 180);
            bounds.extend({ lat, lng });
          }

          // Ajustar la vista para incluir el círculo extendido con un pequeño padding
          mapRef.current.fitBounds(bounds, { padding: 20 });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedPoint, extendedSearch, isGoogleMapsLoaded]);

  // Calcular el índice del punto base seleccionado (siempre 1 ya que es un solo punto)
  const selectedPointIndex = useMemo(() => {
    return 1; // Siempre será el punto base único
  }, []);

  const circleOptions = [
    { radius: 5000, color: "#00FF00", label: "5 km" },
    { radius: 10000, color: "#66B2FF", label: "10 km" },
    { radius: 15000, color: "#FF0000", label: "15 km" },
  ];

  // Generar colores únicos para cada orden
  const orderColors = useMemo(() => {
    const colors = [
      "#FFFF00", // Amarillo
      "#FF00FF", // Magenta
      "#00FF00", // Verde
      "#00FFFF", // Cyan
      "#FFA500", // Naranja
      "#FFC0CB", // Rosa
      "#FF0000", // Rojo
      "#66B2FF", // Azul
      "#A52A2A", // Marrón
      "#808080", // Gris
      "#800080", // Púrpura
      "#000080", // Azul marino
    ];
    const colorMap = {};
    nearbyPoints.forEach((order, index) => {
      colorMap[order.order_id] = colors[index % colors.length];
    });
    return colorMap;
  }, [nearbyPoints]);

  // Estado para almacenar iconos de marcadores procesados
  const [markerIcons, setMarkerIcons] = useState({});
  const [directionsPath, setDirectionsPath] = useState([]);

  // Obtener puntos a mostrar (filtrados por orden seleccionado)
  const pointsToShow = useMemo(() => {
    if (!selectedOrderId) {
      return nearbyPoints.flatMap((p) =>
        p.locations.map((l, i) => ({
          ...l,
          orderId: p.order_id,
          pointIndex: i + 1,
        }))
      );
    }
    const selectedOrder = nearbyPoints.find(
      (p) => p.order_id === selectedOrderId
    );
    if (!selectedOrder) return [];
    return selectedOrder.locations.map((l, i) => ({
      ...l,
      orderId: selectedOrder.order_id,
      pointIndex: i + 1,
    }));
  }, [nearbyPoints, selectedOrderId]);

  useEffect(() => {
    if (!selectedOrderId) {
      setDirectionsPath([]);
      return;
    }

    const selectedOrder = nearbyPoints.find(
      (p) => p.order_id === selectedOrderId
    );
    if (!selectedOrder?.locations || selectedOrder.locations.length < 2) {
      setDirectionsPath([]);
      return;
    }

    const cacheKey = `${selectedOrderId}-${selectedOrder.locations
      .map((loc) => `${loc.latitude}-${loc.longitude}`)
      .join("|")}`;

    if (directionsCache.current[cacheKey]) {
      setDirectionsPath(directionsCache.current[cacheKey]);
      return;
    }

    let isCancelled = false;

    const fetchRoute = async () => {
      try {
        const response = await axiosClient.post("/locations/route", {
          locations: selectedOrder.locations,
        });

        if (isCancelled) return;

        const routePath = response.data?.data || [];

        // Convertir a formato {lat, lng}
        const formattedPath = routePath.map((point) => ({
          lat: point.lat || point.latitude,
          lng: point.lng || point.longitude,
        }));

        directionsCache.current[cacheKey] = formattedPath;
        if (!isCancelled) {
          setDirectionsPath(formattedPath);
        }
      } catch (error) {
        console.error("Error fetching route:", error);
        if (!isCancelled) {
          setDirectionsPath([]);
        }
      }
    };

    fetchRoute();

    return () => {
      isCancelled = true;
    };
  }, [selectedOrderId, nearbyPoints]);

  // Función para crear un icono con borde ajustado al contorno interior de la imagen
  const createMarkerIconWithBorder = useCallback((imageUrl, borderColor) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const borderWidth = 8; // Grosor del borde aumentado para mayor visibilidad
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Canvas con espacio para el borde
        canvas.width = img.width + borderWidth * 2;
        canvas.height = img.height + borderWidth * 2;

        // Método simplificado y eficiente para crear borde que sigue el contorno
        ctx.save();

        // Crear canvas para el borde
        const borderCanvas = document.createElement("canvas");
        borderCanvas.width = canvas.width;
        borderCanvas.height = canvas.height;
        const borderCtx = borderCanvas.getContext("2d");

        // Método mejorado: usar shadow blur para crear borde que sigue el contorno
        // Dibujar la imagen con sombra del color del borde (más intensa)
        borderCtx.shadowColor = borderColor;
        borderCtx.shadowBlur = borderWidth * 3; // Aumentado para borde más visible
        borderCtx.shadowOffsetX = 0;
        borderCtx.shadowOffsetY = 0;

        // Dibujar la imagen con sombra (esto crea el efecto de borde)
        borderCtx.drawImage(
          img,
          borderWidth,
          borderWidth,
          img.width,
          img.height
        );

        // Limpiar la sombra
        borderCtx.shadowBlur = 0;
        borderCtx.shadowColor = "transparent";

        // Crear máscara para que el borde solo aparezca fuera de la imagen
        // Dibujar la imagen original encima para "recortar" el borde interno
        borderCtx.globalCompositeOperation = "destination-over";
        borderCtx.drawImage(
          img,
          borderWidth,
          borderWidth,
          img.width,
          img.height
        );

        // Cambiar composición para dibujar la imagen original encima
        borderCtx.globalCompositeOperation = "source-over";
        borderCtx.drawImage(
          img,
          borderWidth,
          borderWidth,
          img.width,
          img.height
        );

        // Copiar el resultado al canvas principal
        ctx.drawImage(borderCanvas, 0, 0);

        ctx.restore();

        resolve(canvas.toDataURL());
      };

      img.onerror = () => resolve(imageUrl);
      img.src = imageUrl;
    });
  }, []);

  // Función helper para crear configuración de icono de forma segura
  const createIconConfig = useCallback((url, size, origin, anchor) => {
    if (window.google?.maps) {
      return {
        url,
        scaledSize: new window.google.maps.Size(size.width, size.height),
        origin: new window.google.maps.Point(origin.x, origin.y),
        anchor: new window.google.maps.Point(anchor.x, anchor.y),
      };
    }
    return {
      url,
      scaledSize: { width: size.width, height: size.height },
      origin: { x: origin.x, y: origin.y },
      anchor: { x: anchor.x, y: anchor.y },
    };
  }, []);

  // Crear iconos de marcadores con bordes cuando cambian los puntos
  useEffect(() => {
    if (!isGoogleMapsLoaded || pointsToShow.length === 0) return;

    const createIcons = async () => {
      const iconPromises = pointsToShow.map(async (point) => {
        const markerUrl = `/assets/img/varo/orders/markerLocation${point.pointIndex}.png`;
        const orderColor = orderColors[point.orderId] || "#000000";
        // Incluir el color en la clave para que se recree cuando cambie el color
        const iconKey = `${point.orderId}-${point.id}-${point.pointIndex}-${orderColor}`;

        if (!markerIcons[iconKey]) {
          const iconDataUrl = await createMarkerIconWithBorder(
            markerUrl,
            orderColor
          );
          return { key: iconKey, dataUrl: iconDataUrl };
        }
        return null;
      });

      const newIcons = await Promise.all(iconPromises);
      const iconsToAdd = newIcons.filter(Boolean).reduce((acc, icon) => {
        if (icon) acc[icon.key] = icon.dataUrl;
        return acc;
      }, {});

      if (Object.keys(iconsToAdd).length > 0) {
        setMarkerIcons((prev) => ({ ...prev, ...iconsToAdd }));
      }
    };

    createIcons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pointsToShow,
    orderColors,
    isGoogleMapsLoaded,
    createMarkerIconWithBorder,
  ]);

  return (
    <div className="container-fluid p-4">
      <div className="mb-4">
        <h4 className="mb-0">Mapa de ubicaciones cercanas</h4>
        <p className="text-muted small">
          Busca un punto base y encuentra órdenes cercanas
        </p>
      </div>

      <div className="mb-3">
        <MapFilters
          values={null}
          filters={filters}
          setFilters={setFilters}
          onSearch={handleSearch}
          hideSearchButton={true}
        />
      </div>

      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        onLoad={() => {
          // Callback cuando LoadScript ha cargado la librería
          try {
            if (
              typeof window !== "undefined" &&
              window.google &&
              typeof window.google === "object" &&
              window.google.maps &&
              typeof window.google.maps === "object"
            ) {
              setIsGoogleMapsLoaded(true);
            }
          } catch (error) {
            console.warn("Error in LoadScript onLoad:", error);
          }
        }}
        onError={() => {
          console.error("Error loading Google Maps API");
          setIsGoogleMapsLoaded(false);
        }}
      >
        {loading || !isGoogleMapsLoaded ? (
          <>
            <div className="text-center p-5">
              <Loader isLoading={true} />
              {/* <p className="text-muted mt-3">Cargando mapa...</p> */}
            </div>
            <NearbyOrdersMapsSkeleton />
          </>
        ) : (
          <>
            {/* Búsqueda de punto base en la parte superior */}
            <div className="mb-3">
              <BasePointSearch
                selectedPoint={selectedPoint}
                onSelectPoint={(location) => {
                  handleSelectPoint(location);
                  setRightClickCoordinates(null); // Limpiar coordenadas del clic derecho cuando se selecciona un punto
                }}
                onSearchValueChange={(value) => {
                  setBasePointSearchValue(value);
                  // Si se cambia el valor del input manualmente (no desde una selección), limpiar las coordenadas del clic derecho
                  // Solo limpiar si el valor es diferente Y no es una coordenada válida que coincida con rightClickCoordinates
                  const coordinateRegex = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;
                  const isCoordinate = coordinateRegex.test(value.trim());

                  if (value !== rightClickCoordinates?.coordinatesString) {
                    // Si es una coordenada y coincide con rightClickCoordinates, no limpiar
                    if (isCoordinate && rightClickCoordinates) {
                      const [lat, lng] = value
                        .split(",")
                        .map((c) => parseFloat(c.trim()));
                      if (
                        Math.abs(lat - rightClickCoordinates.latitude) <
                          0.0001 &&
                        Math.abs(lng - rightClickCoordinates.longitude) < 0.0001
                      ) {
                        // Las coordenadas coinciden, no limpiar
                        return;
                      }
                    }
                    // Si no coincide o no es una coordenada, limpiar
                    setRightClickCoordinates(null);
                  }
                }}
                onSearchRef={basePointSearchRef}
                autoSelectSingleResult={false}
                externalSearchValue={basePointSearchValue}
                rightClickCoordinates={rightClickCoordinates}
                onClearRightClickCoordinates={() =>
                  setRightClickCoordinates(null)
                }
                onSetRightClickCoordinates={(coordinates) => {
                  setRightClickCoordinates(coordinates);
                  // Actualizar también el input con las coordenadas
                  if (coordinates?.coordinatesString) {
                    setBasePointSearchValue(coordinates.coordinatesString);
                  }
                }}
                onSearchResultsChange={(results) => {
                  setSearchResults(results || []);
                }}
                renderExternalButton={() => {
                  const hasCraneTypes = filters.craneTypes && filters.craneTypes.length > 0;
                  const canSearch =
                    hasCraneTypes &&
                    (selectedPoint ||
                      basePointSearchValue.trim() ||
                      rightClickCoordinates ||
                      hasFilters());
                  return (
                    <Button
                      variant="primary"
                      onClick={handleCombinedSearch}
                      disabled={!canSearch}
                      style={{ height: "38px", minWidth: "120px" }}
                    >
                      <i className="fa fa-search me-2"></i>
                      Buscar
                    </Button>
                  );
                }}
              />
            </div>
            <Row>
              {/* Panel lateral */}
              <Col md={4}>
                <div className="p-2 bg-light rounded shadow-sm pb-3">
                  {selectedPoint && (
                    <>
                      <h6 className="fw-bold mb-2">Radio de búsqueda</h6>
                      <div className="d-flex gap-2 mb-3 flex-wrap">
                        {circleOptions.map((option) => {
                          const isSelected =
                            !showAllRadii && radius === option.radius;
                          const colorMap = {
                            5000: "#008000", // verde oscuro
                            10000: "#66B2FF", // azul
                            15000: "#FF0000", // rojo
                          };
                          const textColorMap = {
                            5000: "#006600", // verde oscuro para texto
                            10000: "#0000CC", // azul oscuro para texto
                            15000: "#CC0000", // rojo oscuro para texto
                          };
                          const btnColor = colorMap[option.radius];
                          const textColor = textColorMap[option.radius];

                          return (
                            <Button
                              key={option.radius}
                              onClick={() => {
                                setShowAllRadii(false);
                                setRadius(option.radius);
                                // El useEffect se encargará de actualizar los puntos inmediatamente
                              }}
                              style={{
                                backgroundColor: isSelected
                                  ? btnColor
                                  : "transparent",
                                borderColor: btnColor,
                                color: isSelected ? "#FFF" : textColor,
                                borderWidth: isSelected ? "3px" : "2px",
                                fontWeight: isSelected ? "bold" : "normal",
                                boxShadow: isSelected
                                  ? `0 0 8px ${btnColor}`
                                  : "none",
                                padding: "8px 16px",
                                fontSize: "14px",
                              }}
                            >
                              {option.label}
                            </Button>
                          );
                        })}
                        <Button
                          onClick={() => {
                            setShowAllRadii(true);
                            // El useEffect se encargará de actualizar los puntos
                          }}
                          style={{
                            backgroundColor: showAllRadii
                              ? "#800080"
                              : "transparent",
                            borderColor: "#800080",
                            color: showAllRadii ? "#FFF" : "#800080",
                            borderWidth: showAllRadii ? "3px" : "2px",
                            fontWeight: showAllRadii ? "bold" : "normal",
                            boxShadow: showAllRadii
                              ? "0 0 8px #800080"
                              : "none",
                            padding: "8px 16px",
                            fontSize: "14px",
                          }}
                        >
                          Todos
                        </Button>
                      </div>
                    </>
                  )}
                  {/* Solo mostrar la lista cuando hay un punto base seleccionado y se ha hecho la búsqueda */}
                  {selectedPoint && (
                    <NearbyOrdersList
                      nearbyPoints={nearbyPoints}
                      orderColors={orderColors}
                      selectedOrderId={selectedOrderId}
                      setSelectedOrderId={setSelectedOrderId}
                      selectedPoint={selectedPoint}
                    />
                  )}
                  {!selectedPoint && nearbyPoints.length === 0 && (
                    <div className="mt-3">
                      <p className="text-muted small text-center">
                        Selecciona una ubicación y haz clic en "Buscar" para
                        encontrar órdenes
                      </p>
                    </div>
                  )}
                </div>
              </Col>

              {/* Mapa */}
              <Col 
                md={8} 
                ref={mapContainerRef}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
              >
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "600px" }}
                  center={center}
                  zoom={
                    selectedPoint ||
                    rightClickCoordinates ||
                    (searchResults && searchResults.length > 0)
                      ? 12
                      : 11
                  }
                  onLoad={(map) => {
                    // Guardar la referencia del mapa
                    mapRef.current = map;
                    
                    // Prevenir el menú contextual del navegador en el contenedor del mapa (protección adicional)
                    const mapContainer = map.getDiv();
                    if (mapContainer) {
                      const handleContextMenu = (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      };
                      mapContainer.addEventListener("contextmenu", handleContextMenu, true);
                    }
                    
                    // Verificación adicional cuando el mapa está completamente cargado
                    // Esto asegura que todas las clases necesarias están disponibles
                    try {
                      if (
                        typeof window !== "undefined" &&
                        window.google &&
                        typeof window.google === "object" &&
                        window.google.maps &&
                        typeof window.google.maps === "object" &&
                        typeof window.google.maps.Size === "function" &&
                        typeof window.google.maps.Point === "function" &&
                        typeof window.google.maps.Marker === "function" &&
                        typeof window.google.maps.Circle === "function"
                      ) {
                        setIsGoogleMapsLoaded(true);
                      } else {
                        console.warn(
                          "Google Maps loaded but required classes are not available"
                        );
                      }
                    } catch (error) {
                      console.warn("Error in GoogleMap onLoad:", error);
                    }
                  }}
                  onRightClick={handleMapRightClick}
                >
                  {/* Marcadores de resultados de búsqueda con icono de "?" */}
                  {searchResults &&
                    searchResults.length > 0 &&
                    isGoogleMapsLoaded &&
                    !selectedPoint &&
                    searchResults.map((result, index) => (
                      <Marker
                        key={`search_result_${index}`}
                        position={{
                          lat: Number(result.latitude),
                          lng: Number(result.longitude),
                        }}
                        onClick={() => handleSelectSearchResult(result)}
                        icon={createIconConfig(
                          `/assets/img/varo/orders/markerQuestionMarkDestination.png`,
                          { width: 60, height: 60 },
                          { x: 0, y: 0 },
                          { x: 30, y: 60 }
                        )}
                        options={{
                          clickable: true,
                          zIndex: 999,
                        }}
                      />
                    ))}

                  {/* Marcador temporal para coordenadas del clic derecho */}
                  {rightClickCoordinates &&
                    !selectedPoint &&
                    isGoogleMapsLoaded && (
                      <Marker
                        position={{
                          lat: Number(rightClickCoordinates.latitude),
                          lng: Number(rightClickCoordinates.longitude),
                        }}
                        draggable={true}
                        onDragEnd={(e) => {
                          const newLat = e.latLng.lat();
                          const newLng = e.latLng.lng();

                          // Formatear coordenadas para el input
                          const coordinatesString = `${newLat}, ${newLng}`;
                          setBasePointSearchValue(coordinatesString);

                          // Limpiar resultados de búsqueda cuando se arrastra el marcador
                          setSearchResults([]);

                          // Actualizar las coordenadas INMEDIATAMENTE para movimiento fluido
                          setRightClickCoordinates({
                            latitude: newLat,
                            longitude: newLng,
                            direccion: rightClickCoordinates?.direccion || "", // Mantener dirección anterior mientras se carga la nueva
                            coordinatesString: coordinatesString,
                          });

                          // Obtener la dirección formateada de forma asíncrona en segundo plano
                          // (sin bloquear la actualización visual)
                          axios
                            .get(
                              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newLat}&lon=${newLng}`
                            )
                            .then((response) => {
                              const formattedAddress =
                                response.data.display_name || "";

                              // Actualizar solo la dirección cuando esté lista
                              setRightClickCoordinates((prev) => ({
                                ...prev,
                                direccion: formattedAddress,
                              }));
                            })
                            .catch((error) => {
                              console.error(
                                "Error al obtener dirección:",
                                error
                              );
                              // Si hay error, mantener las coordenadas pero limpiar la dirección
                              setRightClickCoordinates((prev) => ({
                                ...prev,
                                direccion: "",
                              }));
                            });
                        }}
                        icon={createIconConfig(
                          `/assets/img/varo/orders/markerTarifa1.png`,
                          { width: 60, height: 60 },
                          { x: 0, y: 0 },
                          { x: 30, y: 60 }
                        )}
                        options={{
                          clickable: false,
                          zIndex: 1000,
                        }}
                      />
                    )}

                  {selectedPoint && isGoogleMapsLoaded && (
                    <>
                      {/* Mostrar círculos según el modo seleccionado */}
                      {showAllRadii
                        ? // Mostrar los 3 círculos cuando está en modo "Todos"
                          circleOptions.map((c, i) => (
                            <Circle
                              key={i}
                              center={center}
                              radius={c.radius}
                              options={{
                                strokeColor: c.color,
                                fillColor: c.color,
                                fillOpacity: 0.2,
                              }}
                            />
                          ))
                        : // Mostrar solo el círculo del radio seleccionado
                          circleOptions
                            .filter((c) => c.radius === radius)
                            .map((c, i) => (
                              <Circle
                                key={i}
                                center={center}
                                radius={c.radius}
                                options={{
                                  strokeColor: c.color,
                                  fillColor: c.color,
                                  fillOpacity: 0.2,
                                }}
                              />
                            ))}

                      {/* Círculo de búsqueda extendida (100 km) - negro claro */}
                      {extendedSearch && extendedSearch.radius && (
                        <Circle
                          key="extended-search-circle"
                          center={center}
                          radius={extendedSearch.radius}
                          options={{
                            strokeColor: "#4F4F4F", // Negro claro
                            fillColor: "#4F4F4F", // Negro claro
                            fillOpacity: 0.35, // Opacidad más baja para que sea más sutil
                            strokeWeight: 2,
                          }}
                        />
                      )}

                      {/* Punto base */}
                      {isGoogleMapsLoaded && (
                        <Marker
                          position={center}
                          icon={createIconConfig(
                            `/assets/img/varo/orders/markerTarifa${selectedPointIndex}.png`,
                            { width: 60, height: 60 },
                            { x: 0, y: 0 },
                            { x: 30, y: 60 }
                          )}
                          options={{
                            clickable: false,
                            zIndex: 1000, // Asegurar que esté visualmente por delante
                          }}
                        />
                      )}

                      {/* Puntos cercanos */}
                      {isGoogleMapsLoaded &&
                        pointsToShow.map((point) => {
                          const markerUrl = `/assets/img/varo/orders/markerLocation${point.pointIndex}.png`;
                          const orderColor =
                            orderColors[point.orderId] || "#000000";
                          // Incluir el color en la clave para que coincida con la creación del icono
                          const iconKey = `${point.orderId}-${point.id}-${point.pointIndex}-${orderColor}`;
                          const processedIconUrl =
                            markerIcons[iconKey] || markerUrl;

                          const iconConfig = createIconConfig(
                            processedIconUrl,
                            { width: 60, height: 60 },
                            { x: 0, y: 0 },
                            { x: 30, y: 60 }
                          );

                          return (
                            <Marker
                              key={`${point.orderId}-${point.id}`}
                              position={{
                                lat: parseFloat(point.latitude),
                                lng: parseFloat(point.longitude),
                              }}
                              icon={iconConfig}
                              onClick={() => {
                                // Si ya está seleccionada, deseleccionar; si no, seleccionar
                                setSelectedOrderId(
                                  selectedOrderId === point.orderId
                                    ? null
                                    : point.orderId
                                );
                              }}
                            />
                          );
                        })}

                      {isGoogleMapsLoaded && directionsPath.length > 1 && (
                        <Polyline
                          path={directionsPath}
                          options={{
                            strokeColor: "#1a73e8",
                            strokeOpacity: 0.9,
                            strokeWeight: 5,
                            geodesic: true,
                          }}
                        />
                      )}
                    </>
                  )}
                </GoogleMap>
              </Col>
            </Row>
          </>
        )}
      </LoadScript>
    </div>
  );
};

export default NearbyOrdersMaps;
